import React from 'react';
import {useIntl} from 'react-intl';
import {Button, EMPHASIS, FlexRow, Table, Text} from '@forecast-it/design-system';
import {NovaDeployCompanyRow} from './novaDeploymentData';
import {companyLabel, DeployDirectory} from './novaDeployDirectory';
import {useId} from '../../../../forecast-app/shared/hooks/useId';
import {NovaVisuallyHidden} from '../shared/novaVisuallyHidden';

export interface NovaDeployAudienceTableProps {
	rows: NovaDeployCompanyRow[];
	directory: DeployDirectory;
	/** Called with the row's encoded company id, to open that company's editor. */
	onEditCompany: (companyId: string) => void;
	/** A mutation is in flight — the row action goes inert; the rows shown do not. */
	isSubmitting: boolean;
}

/**
 * Resolves a row's display name the way the rest of the tab names a company — `<numericId> - <name>`
 * — falling back to the raw id when the directory has not arrived yet. An id names nobody, but it is
 * at least true, and the tab renders before any picker has opened the directory fetch.
 */
function companyDisplayName(companyId: string, directory: DeployDirectory): string {
	const company = directory.companies.find(candidate => candidate.companyId === companyId);
	return company ? companyLabel(company) : companyId;
}

function UsersCell({row, directory}: {row: NovaDeployCompanyRow; directory: DeployDirectory}): JSX.Element {
	const {formatMessage} = useIntl();
	if (row.hasCompanyGrant) {
		// The company holds a wholesale grant, which covers anyone named individually too — so the row
		// reads "All users" regardless of who else it lists (see novaDeploymentData's own comment on why
		// both can be true on one row).
		return (
			<Text data-cy={`nova-deploy-row-users-${row.companyId}`}>{formatMessage({id: 'nova.deploy.table.allUsers'})}</Text>
		);
	}
	const people = (directory.personsByCompanyId.get(row.companyId) ?? []).filter(person =>
		row.personIds.includes(person.personId)
	);
	return (
		<FlexRow data-cy={`nova-deploy-row-users-${row.companyId}`} wrap gap="s">
			{people.map(person => (
				<Text key={person.personId}>{person.label}</Text>
			))}
		</FlexRow>
	);
}

function EditCell({
	row,
	onEditCompany,
	isSubmitting,
}: {
	row: NovaDeployCompanyRow;
	onEditCompany: (companyId: string) => void;
	isSubmitting: boolean;
}): JSX.Element {
	const {formatMessage} = useIntl();
	const labelId = useId();
	return (
		<>
			<NovaVisuallyHidden id={labelId}>{formatMessage({id: 'nova.deploy.table.edit'})}</NovaVisuallyHidden>
			<Button
				data-cy={`nova-deploy-row-edit-${row.companyId}`}
				emphasis={EMPHASIS.LOW}
				icon="edit"
				aria-labelledby={labelId}
				disabled={isSubmitting}
				onPress={() => onEditCompany(row.companyId)}
			/>
		</>
	);
}

/**
 * The audience table shown under the bar: one row per company, its users, and an edit control.
 *
 * Three columns only — Company, Users, and an unlabelled actions column. No opted-out column: the
 * product has no such state.
 *
 * Every column below carries an `accessorKey` that names no real field on a row. The design-system
 * `Table` requires one, and renders whatever value it resolves to as plain text next to a column's
 * `icon` render prop — pointing it at a real field would print that field's raw value in the cell
 * alongside the content built here. `icon` accepts arbitrary JSX rather than only a glyph, which is
 * what makes it possible to give a cell content the declarative column API otherwise has no room for:
 * a row-and-column-specific test hook, and — for Users — more than a single string.
 */
// Just wide enough for the row's edit control. Named because the two content columns are sized
// against it — they split what it leaves rather than the whole table.
const ACTIONS_COLUMN_WIDTH = '56px';

export const NovaDeployAudienceTable = ({
	rows,
	directory,
	onEditCompany,
	isSubmitting,
}: NovaDeployAudienceTableProps): JSX.Element => {
	const {formatMessage} = useIntl();
	const data = rows.map(row => ({...row, 'data-cy': `nova-deploy-row-${row.companyId}`}));

	return (
		// Full width so the table's edges line up with the bar above it, and the two content columns
		// share that width evenly. The actions column takes a fixed slice first, so "half" means half
		// of what is actually left rather than half of the table plus an overflowing third column.
		<Table data-cy="nova-deploy-audience-table" data={data} width="100%">
			<Table.TextColumn
				width={`calc((100% - ${ACTIONS_COLUMN_WIDTH}) / 2)`}
				accessorKey="companyDisplay"
				header={formatMessage({id: 'nova.deploy.table.company'})}
				icon={(row: NovaDeployCompanyRow) => (
					<Text data-cy={`nova-deploy-row-company-${row.companyId}`}>
						{companyDisplayName(row.companyId, directory)}
					</Text>
				)}
			/>
			<Table.TextColumn
				width={`calc((100% - ${ACTIONS_COLUMN_WIDTH}) / 2)`}
				accessorKey="usersDisplay"
				header={formatMessage({id: 'nova.deploy.table.users'})}
				icon={(row: NovaDeployCompanyRow) => <UsersCell row={row} directory={directory} />}
			/>
			<Table.TextColumn
				width={ACTIONS_COLUMN_WIDTH}
				accessorKey="rowActions"
				header=""
				icon={(row: NovaDeployCompanyRow) => (
					<EditCell row={row} onEditCompany={onEditCompany} isSubmitting={isSubmitting} />
				)}
			/>
		</Table>
	);
};
